import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useExportConfig } from '../../hooks/useExportConfig';
import { useExportHandlers } from '../../hooks/useExportHandlers';
import { useExportQuota } from '../../hooks/useExportQuota';
import { useCustomExport } from '../../hooks/useCustomExport';
import { type ExportDateRange, type ReportType } from '../../types';
import styled, { keyframes } from 'styled-components';
import { BsFiletypeCsv, BsThreeDotsVertical } from 'react-icons/bs';
import { FiSettings } from 'react-icons/fi';
import { Dropdown } from 'react-bootstrap';
import { ExportModal } from '../exportDialog';
import CustomExportDialog, { type CustomExportColumn } from '../customExportDialog';
import { exportRefreshEventEmitter } from '../../services/exportRefreshEventEmitter';
import ReportDataContext from '../../../reports/contexts/reportDataContext';
import { validateExportDateRange } from '../../../../helpers/dateRangePlanLimits';
import type { RootState } from '../../../../state/store';

const buildExportDateRangeFromFilters = (
  dates: [string, string],
  timeRange: [[number, number], [number, number]]
): ExportDateRange => ({
  dates: [dates[0], dates[1]],
  timeRange: [
    [timeRange[0][0], timeRange[0][1]],
    [timeRange[1][0], timeRange[1][1]]
  ]
});

const ExportOptionsMenu: React.FC = () => {
  const [showCustomExportDialog, setShowCustomExportDialog] = useState(false);
  const [exportDateRange, setExportDateRange] = useState<ExportDateRange | null>(null);

  const { filters } = useSelector((state: RootState) => state);
  const { dates, timeRange } = filters;
  const { plan_type: planType } = useSelector((state: RootState) => state.company.company_details);

  // Get isCustomExportEnabled from report data context
  const reportDataContext = useContext(ReportDataContext);
  const isCustomExportEnabled = reportDataContext?.isCustomExportEnabled ?? false;

  // Custom hooks for managing state and logic
  const { exportConfig, loadExportOptions } = useExportConfig();
  const { initiateCustomExport, isExporting } = useCustomExport();
  const {
    quotaModal,
    checkExportQuota,
    showQuotaModal,
    setQuotaData,
    hideQuotaModal,
    setQuotaError,
    setExportProcessing,
    setExportCompleted
  } = useExportQuota();
  const {
    initiateReportExport,
    handleExportSuccess,
    handleExportError,
    handleQuotaCheckError,
    handleViewSavedReportsClick
  } = useExportHandlers();

  // Load export options on component mount
  useEffect(() => {
    loadExportOptions();
  }, [loadExportOptions]);

  // Listen for export completion events
  useEffect(() => {
    const handleExportCompleted = () => {
      // Only update modal if it's currently in processing state
      if (quotaModal.show && quotaModal.exportProcessing) {
        setExportCompleted();
      }
    };

    exportRefreshEventEmitter.onExportCompleted(handleExportCompleted);

    return () => {
      exportRefreshEventEmitter.offExportCompleted(handleExportCompleted);
    };
  }, [quotaModal.show, quotaModal.exportProcessing, setExportCompleted]);

  const handleExportClick = async (reportType: ReportType) => {
    try {
      const initialExportDateRange = buildExportDateRangeFromFilters(
        dates as [string, string],
        timeRange as [[number, number], [number, number]]
      );
      setExportDateRange(initialExportDateRange);

      // Show loading modal
      showQuotaModal(reportType);

      // Check quota first
      const quotaData = await checkExportQuota(reportType, initialExportDateRange);

      // Set quota data and show modal for all users (Pro and Lite)
      setQuotaData(quotaData);
    } catch (error) {
      console.error('Quota check failed:', error);
      setQuotaError();
      handleQuotaCheckError();
    }
  };

  const handleQuotaModalProceed = () => {
    const reportType = quotaModal.reportType;

    if (reportType && exportDateRange) {
      if (!validateExportDateRange(exportDateRange.dates, exportDateRange.timeRange, planType || 'lite')) {
        return;
      }

      // Set processing state instead of hiding modal
      setExportProcessing();

      initiateReportExport(reportType, exportDateRange)
        .then((response) => {
          if (response.success) {
            handleExportSuccess();
            // Keep modal open in processing state
            // It will be updated when export completes via Pusher
          }
        })
        .catch((error) => {
          // Hide modal on error
          hideQuotaModal();
          handleExportError(error, reportType, () => handleExportClick(reportType));
        });
    }
  };

  const handleDownloadExisting = (downloadUrl: string) => {
    hideQuotaModal();

    // Create a temporary anchor element for direct download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = ''; // This will use the filename from the URL
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCustomExportClick = () => {
    setShowCustomExportDialog(true);
  };

  const handleCustomExportClose = () => {
    setShowCustomExportDialog(false);
  };

  const handleCustomExport = async (selectedColumns: CustomExportColumn[], reportName: string) => {
    try {
      await initiateCustomExport(selectedColumns, reportName);
      // Don't call handleExportSuccess here - the hook already shows a Swal popup
      // The dialog stays open showing "Export In Progress" state
    } catch (error) {
      console.error('Custom export failed:', error);
      // On error, the hook shows an error Swal and the dialog stays open
    }
  };

  return (
    <>
      <StyledDropdown>
        <Dropdown.Toggle as={IconButton}>
          <BsFiletypeCsv size={20} />
          <span>Export</span>
          <BsThreeDotsVertical size={15} />
        </Dropdown.Toggle>
        <StyledDropdownMenu>
          <DropdownHeader>REPORT TYPE</DropdownHeader>
          {exportConfig.isLoading && (
            <DropdownItem $disabled>Loading export options...</DropdownItem>
          )}
          {exportConfig.isError && <DropdownItem $disabled>Failed to load options</DropdownItem>}
          {exportConfig.data.length > 0
            ? exportConfig.data
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((reportType) => (
                  <Dropdown.Item key={reportType.url} onClick={() => handleExportClick(reportType)}>
                    {reportType.name}
                  </Dropdown.Item>
                ))
            : null}
          {isCustomExportEnabled && (
            <>
              <DropdownDivider />
              <CustomExportItem onClick={handleCustomExportClick}>
                <FiSettings size={14} />
                <span>Create Custom Report</span>
              </CustomExportItem>
            </>
          )}
          <DropdownDivider />
          <Dropdown.Item onClick={handleViewSavedReportsClick}>View Exported Reports</Dropdown.Item>
        </StyledDropdownMenu>
      </StyledDropdown>

      <ExportModal
        quotaModal={quotaModal}
        exportDateRange={exportDateRange}
        onExportDateRangeChange={setExportDateRange}
        onClose={hideQuotaModal}
        onProceed={handleQuotaModalProceed}
        onDownloadExisting={handleDownloadExisting}
        onViewSavedReports={handleViewSavedReportsClick}
      />

      <CustomExportDialog
        show={showCustomExportDialog}
        onClose={handleCustomExportClose}
        onExport={handleCustomExport}
        isExporting={isExporting}
      />
    </>
  );
};

export default ExportOptionsMenu;

const fadeInTop = keyframes`
  0% {
    -webkit-transform: translateY(-1px);
    transform: translateY(-1px);
    opacity: 0;
  }
  100% {
    -webkit-transform: translateY(-50);
    transform: translateY(-50);
    opacity: 1;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .dropdown-toggle {
    width: 100%;
    border: none;
    &::after {
      display: none;
    }
    &:focus {
      box-shadow: none;
    }
  }
`;

const IconButton = styled.button`
  background: ${({ theme }) => theme.dropdown.background_color};
  color: ${({ theme }) => theme.text};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  cursor: pointer;
  padding: 8px 12px;
  margin: 0;
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  &.show {
    color: #fff;
  }

  &:hover {
    background: ${({ theme }) => theme.button_plain.hover};
    color: ${({ theme }) => theme.text};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.primary_color};
    outline-offset: 2px;
  }
  &:active {
    outline: 2px solid ${({ theme }) => theme.primary_color};
    border: 1px solid ${({ theme }) => theme.primary_color};
    outline-offset: 2px;
  }

  span {
    margin-left: 8px;
    margin-right: auto;
  }
`;

const StyledDropdownMenu = styled(Dropdown.Menu)`
  animation: ${fadeInTop} 0.2s;
  min-width: 220px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 12px;
  box-shadow:
    0px 10px 38px -10px rgba(22, 23, 24, 0.35),
    0px 10px 20px -15px rgba(22, 23, 24, 0.2);
  background-color: ${({ theme }) => theme.dropdown.background_color};

  .dropdown-item {
    background-color: transparent;
    color: ${({ theme }) => theme.text};
    border: none;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    &:hover {
      background-color: ${({ theme }) => theme.primary_color};
      color: white;
      transform: translateX(2px);
    }

    &:focus {
      background-color: ${({ theme }) => theme.primary_color};
      color: white;
      box-shadow: none;
    }

    &:active {
      background-color: ${({ theme }) => theme.primary_color};
      color: white;
    }
  }
`;

const DropdownHeader = styled.div`
  font-size: 12px;
  line-height: 1.2;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  padding: 4px 12px;
  margin-bottom: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownDivider = styled.hr`
  height: 1px;
  background-color: ${({ theme }) => theme.border_color};
  border: none;
  margin: 8px 4px;
  opacity: 0.3;
`;

const DropdownItem = styled.div<{ $disabled?: boolean }>`
  background-color: transparent;
  color: ${({ theme }) => theme.text};
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: ${({ theme, $disabled }) =>
      $disabled ? 'transparent' : theme.primary_color};
    color: ${({ $disabled }) => ($disabled ? 'inherit' : 'white')};
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateX(2px)')};
  }
`;

const CustomExportItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  color: ${({ theme }) => theme.text};
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  > span {
    font-size: 14px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.primary_color};
    color: white;
    transform: translateX(2px);
  }
`;
